import type { AddressLookupRow, TrafficTopRow } from '@bartizo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { lookupAddresses } from '../api/addresses.js';
import { getTrafficTop, type TrafficScope } from '../api/traffic.js';
import { formatPercent } from '../lib/format.js';
import { botLabel, CATEGORY_LABELS, hostLabel } from '../lib/traffic.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { DataTable, Facts, ShareBar, type Column } from '../ui/DataTable.js';
import { Dialog } from '../ui/Dialog.js';
import { AddressLink, AddressStatusBadge } from './AddressStatus.js';
import { EmptyState } from './EmptyState.js';

/**
 * The sites behind the traffic (knowledge doc section 8.7; P11.7): which got the
 * requests, how many of them were refused, and — a click away — what came to one
 * site: its pages, the addresses that asked, the answers they got, who they were.
 *
 * A host that is no real site — none matched, an address asked for by number —
 * says so in words, because that is where scanners show first.
 */

const count = (value: number): string => Math.round(value).toLocaleString('en-US');

const refusedShare = (row: TrafficTopRow): number =>
  row.requests === 0 ? 0 : row.status4xx / row.requests;

function Refused({ row }: { row: TrafficTopRow }): ReactNode {
  const share = refusedShare(row);
  return (
    <span className={share >= 0.5 && row.requests >= 20 ? 'text-danger' : undefined}>
      {formatPercent(share * 100, 0)}
      <span className="cell-sub">{count(row.status4xx)} 4xx</span>
    </span>
  );
}

export function SiteName({ host }: { host: string }): ReactNode {
  const label = hostLabel(host);
  return (
    <>
      <span className="host-name">{label.name}</span>
      {label.odd && <span className="badge badge-warning">check</span>}
      {label.note !== undefined && <span className="cell-sub">{label.note}</span>}
    </>
  );
}

export interface SiteActions {
  /** The chosen server, when there is one: rules and bans are made for it. */
  serverId?: string | undefined;
  /** A ban offered beside an address, where the server can enforce one. */
  onBan?: ((ip: string) => void) | undefined;
}

export function SitesCard({
  scope,
  actions,
}: {
  scope: TrafficScope;
  actions: SiteActions;
}): ReactNode {
  const sites = useQuery({
    queryKey: ['traffic-top', scope, 'host'],
    queryFn: () => getTrafficTop(scope, 'host', 20),
    placeholderData: keepPreviousData,
  });
  const [open, setOpen] = useState<TrafficTopRow | null>(null);
  const rows = sites.data?.rows ?? [];
  const total = rows.reduce((sum, row) => sum + row.requests, 0);

  const columns: Column<TrafficTopRow>[] = [
    {
      key: 'site',
      header: 'Site',
      rowHeader: true,
      sortValue: (row) => row.key,
      cell: (row) => <SiteName host={row.key} />,
    },
    {
      key: 'requests',
      header: 'Requests',
      align: 'end',
      sortValue: (row) => row.requests,
      cell: (row) => <ShareBar value={row.requests} total={total} />,
    },
    {
      key: 'refused',
      header: 'Refused',
      align: 'end',
      sortValue: refusedShare,
      cell: (row) => <Refused row={row} />,
    },
    {
      key: 'errors',
      header: 'Errors',
      align: 'end',
      wideOnly: true,
      sortValue: (row) => row.status5xx,
      cell: (row) => count(row.status5xx),
    },
    {
      key: 'speed',
      header: 'Avg time',
      align: 'end',
      wideOnly: true,
      sortValue: (row) => row.avgResponseMs,
      cell: (row) => `${count(row.avgResponseMs)} ms`,
    },
  ];

  return (
    <Card
      title="Sites"
      description="Which sites got the requests, and how many were refused. Open one to see what came to it."
      aside={
        <Link to={`/traffic/host${window.location.search}`} className="text-sm">
          View all →
        </Link>
      }
    >
      {sites.isError && <Alert tone="danger">Could not load the sites.</Alert>}
      {sites.data !== undefined && (
        <DataTable
          caption="Sites by requests"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.key}
          onSelect={setOpen}
          selectedKey={open?.key}
          initialSort={{ key: 'requests', direction: 'desc' }}
          empty={<EmptyState title="No requests in this range">Pick a longer range.</EmptyState>}
        />
      )}
      {open !== null && (
        <SitePanel
          site={open}
          scope={scope}
          actions={actions}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </Card>
  );
}

function useSiteTop(
  scope: TrafficScope,
  host: string,
  dimension: Parameters<typeof getTrafficTop>[1],
) {
  return useQuery({
    queryKey: ['traffic-site', scope, host, dimension],
    queryFn: () => getTrafficTop(scope, dimension, 15, host),
  });
}

export function SitePanel({
  site,
  scope,
  actions,
  onClose,
}: {
  site: TrafficTopRow;
  scope: TrafficScope;
  actions: SiteActions;
  onClose: () => void;
}): ReactNode {
  const label = hostLabel(site.key);
  const paths = useSiteTop(scope, site.key, 'site_path');
  const ips = useSiteTop(scope, site.key, 'site_ip');
  const statuses = useSiteTop(scope, site.key, 'site_status');
  const categories = useSiteTop(scope, site.key, 'site_category');
  const bots = useSiteTop(scope, site.key, 'site_bot');

  const addresses = (ips.data?.rows ?? []).map((row) => row.key);
  const verdicts = useQuery({
    queryKey: ['address-lookup', addresses],
    queryFn: () => lookupAddresses(addresses),
    enabled: addresses.length > 0,
  });
  const verdictOf = new Map<string, AddressLookupRow>(
    (verdicts.data ?? []).map((entry) => [entry.ip, entry]),
  );

  const nothing = paths.data?.rows.length === 0 && ips.data?.rows.length === 0;

  const ruleLink = (path: string): string => {
    const params = new URLSearchParams({ path });
    if (!label.odd) params.set('site', site.key);
    if (actions.serverId !== undefined) params.set('server', actions.serverId);
    return `/rules?${params.toString()}`;
  };

  const pathColumns: Column<TrafficTopRow>[] = [
    {
      key: 'path',
      header: 'Page',
      rowHeader: true,
      cell: (row) => <code>{row.key}</code>,
    },
    {
      key: 'requests',
      header: 'Requests',
      align: 'end',
      sortValue: (row) => row.requests,
      cell: (row) => count(row.requests),
    },
    {
      key: 'refused',
      header: 'Refused',
      align: 'end',
      sortValue: refusedShare,
      cell: (row) => <Refused row={row} />,
    },
    {
      key: 'act',
      header: 'Next',
      cell: (row) => (
        <Link to={ruleLink(row.key)} className="text-sm">
          Make a rule
        </Link>
      ),
    },
  ];

  const ipColumns: Column<TrafficTopRow>[] = [
    {
      key: 'ip',
      header: 'Address',
      rowHeader: true,
      cell: (row) => {
        const verdict = verdictOf.get(row.key);
        return (
          <>
            <AddressLink ip={row.key} />{' '}
            {verdict !== undefined && <AddressStatusBadge status={verdict.status} />}
          </>
        );
      },
    },
    {
      key: 'requests',
      header: 'Requests',
      align: 'end',
      sortValue: (row) => row.requests,
      cell: (row) => count(row.requests),
    },
    {
      key: 'refused',
      header: 'Refused',
      align: 'end',
      sortValue: refusedShare,
      cell: (row) => <Refused row={row} />,
    },
    ...(actions.onBan === undefined
      ? []
      : [
          {
            key: 'act',
            header: 'Next',
            cell: (row: TrafficTopRow) =>
              verdictOf.get(row.key)?.status === 'stopped' ? (
                <span className="muted text-sm">Stopped</span>
              ) : (
                <button
                  type="button"
                  className="link-button text-sm"
                  onClick={() => {
                    actions.onBan?.(row.key);
                  }}
                >
                  Ban
                </button>
              ),
          },
        ]),
  ];

  const small = (rows: readonly TrafficTopRow[], name: (key: string) => string) =>
    rows.length === 0 ? (
      <p className="muted text-sm">None.</p>
    ) : (
      <ul className="inline-list text-sm">
        {rows.map((row) => (
          <li key={row.key}>
            {name(row.key)} <span className="muted">{count(row.requests)}</span>
          </li>
        ))}
      </ul>
    );

  return (
    <Dialog
      variant="panel"
      eyebrow="Site"
      title={label.name}
      description={label.note}
      onClose={onClose}
    >
      <Facts
        items={[
          ['Requests', count(site.requests)],
          [
            'Refused (4xx)',
            `${formatPercent(refusedShare(site) * 100, 0)} · ${count(site.status4xx)}`,
          ],
          ['Server errors (5xx)', count(site.status5xx)],
          ['Average time', `${count(site.avgResponseMs)} ms`],
        ]}
      />

      {nothing && (
        <Alert tone="info" title="No breakdown for this site in this range">
          Servers send one per site from this release on, for their twenty busiest sites a minute.
        </Alert>
      )}

      <section>
        <h3>Pages asked for</h3>
        <DataTable
          caption={`Pages asked for on ${label.name}`}
          columns={pathColumns}
          rows={paths.data?.rows ?? []}
          rowKey={(row) => row.key}
          initialSort={{ key: 'requests', direction: 'desc' }}
          empty={<p className="muted text-sm">None.</p>}
        />
      </section>

      <section>
        <h3>Who asked</h3>
        <DataTable
          caption={`Addresses that asked ${label.name}`}
          columns={ipColumns}
          rows={ips.data?.rows ?? []}
          rowKey={(row) => row.key}
          initialSort={{ key: 'requests', direction: 'desc' }}
          empty={<p className="muted text-sm">None — addresses are kept 14 days.</p>}
        />
      </section>

      <section className="split">
        <div>
          <h3>Answers</h3>
          {small(statuses.data?.rows ?? [], (key) => key)}
        </div>
        <div>
          <h3>What they were</h3>
          {small(categories.data?.rows ?? [], (key) =>
            Object.hasOwn(CATEGORY_LABELS, key)
              ? CATEGORY_LABELS[key as keyof typeof CATEGORY_LABELS]
              : key,
          )}
        </div>
        <div>
          <h3>Bots</h3>
          {small(bots.data?.rows ?? [], botLabel)}
        </div>
      </section>
    </Dialog>
  );
}
